/**
 * SSR loader for the blog overview: fetches the published post list straight
 * from the REST API (works during SSR and on the client, no Supabase JS client
 * needed) so the article links are present in the server's initial HTML.
 */
import { EXCLUDED_TAGS } from "@/lib/resourceTypes";

export interface BlogListPost {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  featured_image: string | null;
  category: string | null;
  industry_category: string | null;
  service_category: string | null;
  published_at: string | null;
  author: string | null;
  tags: string[] | null;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

const SELECT =
  "id,slug,title,summary,featured_image,category,industry_category,service_category,published_at,author,tags";

const hasExcludedTag = (tags?: string[] | null) =>
  (tags ?? []).some((t) => EXCLUDED_TAGS.includes(t.toLowerCase()));

/** Never throws — a network hiccup just falls back to client-side loading. */
export async function loadBlogList(lang: string): Promise<BlogListPost[]> {
  if (!SUPABASE_URL || !SUPABASE_KEY) return [];
  try {
    const url =
      `${SUPABASE_URL}/rest/v1/blog_posts` +
      `?select=${encodeURIComponent(SELECT)}` +
      `&lang=eq.${encodeURIComponent(lang)}` +
      `&published_at=not.is.null` +
      `&order=published_at.desc&limit=2000`;
    const res = await fetch(url, {
      headers: { apikey: SUPABASE_KEY, Accept: "application/json" },
    });
    if (!res.ok) return [];
    const rows = (await res.json()) as BlogListPost[];
    return Array.isArray(rows) ? rows.filter((p) => !hasExcludedTag(p.tags)) : [];
  } catch {
    return [];
  }
}
